import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AnnotationService,
  AnnotationRemoveEventArgs,
  LoadEventArgs,
  MagnificationService,
  NavigationService,
  PdfViewerComponent,
  PdfViewerModule,
  ToolbarService,
  TextSearchService,
  TextSelectionService,
  CustomStampSettings,
} from '@syncfusion/ej2-angular-pdfviewer';
import { UploaderModule } from '@syncfusion/ej2-angular-inputs';
import { ToolbarModule, ToolbarComponent, MenuEventArgs } from '@syncfusion/ej2-angular-navigations';
import { DropDownButtonModule, ItemModel } from '@syncfusion/ej2-angular-splitbuttons';

import { ApiService } from '../services/api-service';
import { Stamp } from '../models/stamp';


@Component({
  selector: 'app-redact-the-fact',
  templateUrl: './redact-the-fact.html',
  styleUrl: './redact-the-fact.scss',
  imports: [
    PdfViewerModule,
    ToolbarModule,
    UploaderModule,
    DropDownButtonModule,
  ],
  providers: [
    AnnotationService,
    MagnificationService,
    NavigationService,
    ToolbarService,
    TextSearchService,
    TextSelectionService,
  ]
})
export class RedactTheFact {

  // PDF viewer component
  @ViewChild('pdfviewer')
  public pdfviewerControl: PdfViewerComponent | undefined

  // primary toolbar component
  @ViewChild('primaryToolbar')
  public primaryToolbar: ToolbarComponent | undefined;

  // list of redaction annotations
  public redactionList: string[] = [];


  // location of the pdf viewer control files 
  // needs to be located in an accessible folder like 'public' or 'assets'
  //public resource: string = window.location.origin + "/ej2-pdfviewer-lib";
  public resource: string = "https://cdn.syncfusion.com/ej2/25.1.35/dist/ej2-pdfviewer-lib"; // temporarily putting in for build pipeline usage

  // document to load into the pdf viewer
  public document: string = "";

  // Enabling only specific toolbar items in the PDF viewer
  public toolbarSettings: object = {
    showTooltip: true,
    toolbarItems: [
      //'DownloadOption',   // Allows downloading the PDF file
      'OpenOption',  // Allows opening a PDF file
      //'PrintOption',  // Allows printing the PDF file
      //'PageNavigationTool', // Allows page navigation
      //'MagnificationTool',  // Allows zooming in and out
      //'PanTool',  // Allows panning the PDF page
      //'SelectionTool',  // Allows selecting text
      //'SearchOption', // Allows searching text
      //'UndoRedoTool', // Allows undo and redo of annotations
      'AnnotationEditTool', // Allows editing annotations
      //'FormDesignerEditTool', // Allows editing form fields
      //'CommentTool',  // Allows adding comments
      //'SubmitForm'  // Allows submitting form data
    ],
    annotationToolbarItems: [
      //'StampAnnotationTool',      // Add Stamp option
      'HandWrittenSignatureTool'  // Add Signature option
    ]
  };


  // Dropdown options for the stamp button
  public stampsDropdownOptions: ItemModel[] = [
  ];


  public activeStampId: string = "";
  public activeStampImage: string = "";

  // Array to hold the stamp images retrieved from the backend
  public stampsArray: Stamp[];

  public userId: number | undefined;
  public documentId: number | undefined;

  constructor(private apiService: ApiService, private route: ActivatedRoute) {
    this.stampsArray = new Array<Stamp>();

    this.userId = this.route.snapshot.params['userId'];
    this.documentId = this.route.snapshot.params['documentId'];
    console.log("userId:", this.userId, " documentId:", this.documentId);

    // check to make sure documentId is defined
    if (this.documentId === undefined) {
      console.error("No documentId provided in route");
      return;
    }

    this.apiService.getDocument(this.documentId).subscribe({
      next: (response) => {
        console.log('Document loaded successfully:', response);
        this.document = response;
        this.pdfviewerControl?.load(this.document, "");
      },
      error: (error) => {
        console.error('Error loading document:', error);
      }
    });

    this.RetrieveStamps();
  }

  // Retrieve stamp images from the backend API
  private RetrieveStamps(): void {
    if (this.userId === undefined) {
      console.error("No userId provided in route");
      return;
    }
    this.apiService.getAvailableStampsList(this.userId).subscribe({
      next: (response: Stamp[]) => {
        console.log('Stamps retrieved successfully:', response);

        console.log("Type", typeof (response));

        response.forEach((stamp: Stamp) => {
          this.stampsArray.push({ id: stamp.id, name: stamp.name, data: stamp.data, width: stamp.width, height: stamp.height });
          this.stampsDropdownOptions.push({ text: stamp.name.charAt(0).toUpperCase() + stamp.name.slice(1) });
        });
      },
      error: (error) => {
        console.error('Error retrieving stamps:', error);
      }
    });
  }

  public ngOnInit(): void { }

  // After the view initializes, disable the redact buttons if no document is loaded
  public ngAfterViewInit(): void {
    if (this.pdfviewerControl && (this.pdfviewerControl.documentPath === undefined || this.pdfviewerControl.documentPath === "")) {
      if (this.primaryToolbar) {
        this.primaryToolbar.items.filter((item: any) => item.id === 'redact-blackout-button')[0].disabled = true;
        this.primaryToolbar.items.filter((item: any) => item.id === 'redact-whiteout-button')[0].disabled = true;
        this.primaryToolbar.items.filter((item: any) => item.id === 'stamp-dropdown-button')[0].disabled = true;

      }
    }
    this.updateRedaction();
  }


  // black out (redact) button clicked
  public redactBlackOut(): void {
    if (this.pdfviewerControl) {
      this.pdfviewerControl.rectangleSettings = {
        fillColor: '#000000',
        strokeColor: '#000000',
        author: 'Redaction'
      }
      this.pdfviewerControl.annotation.setAnnotationMode('Rectangle');
    }
  }

  // white out (redact) button is clicked
  public redactWhiteOut(): void {
    if (this.pdfviewerControl) {
      this.pdfviewerControl.rectangleSettings = {
        fillColor: '#ffffff',
        strokeColor: '#ffffff',
        author: 'Redaction'
      }
      this.pdfviewerControl.annotation.setAnnotationMode('Rectangle');
    }
  }

  // apply redaction
  public async applyRedaction(): Promise<void> {
    if (this.pdfviewerControl) {
      await this.pdfviewerControl.saveAsBlob().then((blob: Blob) => {
        var fileReader = new FileReader();
        fileReader.readAsDataURL(blob);
        fileReader.onload = async (e: ProgressEvent<FileReader>) => {
          var base64String = e.target?.result;
          const requestData = JSON.stringify({ base64String });
          await this.apiService.redactFile(requestData).subscribe({
            next: (response) => {
              console.log('Redaction applied successfully:', response);
              if (this.pdfviewerControl) {
                this.pdfviewerControl.load(response, "");
              }
            },
            error: (error) => {
              console.error('Error applying redaction:', error);
            }
          });
        }
      });
    }
    this.redactionList = [];
    this.updateRedaction();
  }

  //To enable/disable the redaction button based on count
  public updateRedaction(): void {
    if (this.primaryToolbar)
      if (this.redactionList.length <= 0) {
        this.primaryToolbar.items.filter((item: any) => item.id === 'apply-redaction-button')[0].disabled = true;
      }
      else {
        this.primaryToolbar.items.filter((item: any) => item.id === 'apply-redaction-button')[0].disabled = false;
      }
  }

  // Enable the redact/stamp buttons when document is loaded
  public pdfDocumentLoaded(args: LoadEventArgs): void {
    if (this.primaryToolbar) {
      this.primaryToolbar.items.filter((item: any) => item.id === 'redact-blackout-button')[0].disabled = false;
      this.primaryToolbar.items.filter((item: any) => item.id === 'redact-whiteout-button')[0].disabled = false;
      this.primaryToolbar.items.filter((item: any) => item.id === 'stamp-dropdown-button')[0].disabled = false;
    }
  }

  // When redaction annotation is added, update the list and button state
  public annotationAdd(args: AnnotationRemoveEventArgs): void {
    var annotation = this.pdfviewerControl?.annotationCollection.filter((a: any) => a.annotationId === args.annotationId)[0];
    if (annotation && annotation.author === 'Redaction') {
      this.redactionList.push(args.annotationId);
      this.updateRedaction();
    }
  }

  // When redaction annotation is removed, update the list and button state
  public annotationRemove(args: AnnotationRemoveEventArgs): void {
    if (this.redactionList.indexOf(args.annotationId) !== -1) {
      this.redactionList = this.redactionList.filter(id => id !== args.annotationId);
      this.updateRedaction();
    }
  }

  // When a stamp is selected from the dropdown, set the annotation mode to Stamp with the selected stamp
  public async stampSelect(args: MenuEventArgs) {

    if (this.stampsArray.some(s => s.name.toLowerCase() === args!.item!.text!.toLowerCase())) {
      if (this.pdfviewerControl) {
        const selectedStamp = this.stampsArray.filter(s => s.name.toLowerCase() === args!.item!.text!.toLowerCase())[0];
        var stampImage = "";
        await this.apiService.retrieveStampById(selectedStamp.id).subscribe(
          (response) => {
            stampImage = response;
            this.activeStampId = selectedStamp.id

            this.pdfviewerControl!.annotation.addAnnotation(
              "Stamp",
              {
                author: "Stamp",
                customStamps: [
                  {
                    customStampName: selectedStamp.name,
                    customStampImageSource: stampImage,

                  }
                ],
                width: selectedStamp.width,
                height: selectedStamp.height

              } as CustomStampSettings
            );


          }
        );


      }

    }

  }
  


  public async applyStamp() {
    if (this.pdfviewerControl) {
      await this.pdfviewerControl.saveAsBlob().then((blob: Blob) => {
        var fileReader = new FileReader();
        fileReader.readAsDataURL(blob);
        fileReader.onload = async (e: ProgressEvent<FileReader>) => {
          var base64String = e.target?.result;
          const requestData = JSON.stringify({ base64String });
          await this.apiService.stampDocument(requestData, this.activeStampId).subscribe({
            next: (response) => {
              console.log('Stamp applied successfully:', response);
              if (this.pdfviewerControl) {
                this.pdfviewerControl.load(response, "");
              }
            },
            error: (error) => {
              console.error('Error applying stamp:', error);
            }
          });
        }
      });
    }
  }

}
